import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';

const QUESTIONS = [
  { number: 1, text: 'What can I celebrate about yesterday?' },
  { number: 2, text: 'What did I learn from that?' },
  { number: 3, text: 'What is my pressing goal?' },
  { number: 4, text: 'What is the fear that comes up when I think of that goal?' },
  { number: 5, text: 'What do my core values say to do? Create a list.' },
  { number: 6, text: 'From that list, what action can I take today?' },
];

export function CoreValueConfirmation() {
  const navigate = useNavigate();
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCoreValues = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/');
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('core_value_1, core_value_2, core_value_3')
          .eq('id', user.id)
          .single();

        if (profile) {
          setCoreValues(
            [profile.core_value_1, profile.core_value_2, profile.core_value_3].filter(Boolean)
          );
        }
      } catch (error) {
        console.error('Error fetching core values:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCoreValues();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-[#236caf] text-lg font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <ProtectedLayout>
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-3xl w-full bg-white rounded-xl shadow-lg p-8 space-y-8">

          {/* Opening */}
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-[#004060] leading-snug">
              Congratulations. You now have the three values you can use too increase creativiity, confidence, and energy.
            </h1>
            <p className="text-gray-700 leading-relaxed">
              Most people go their entire lives without this kind of clarity.
            </p>
          </div>

          {/* Core values */}
          <div className="space-y-3">
            <p className="text-xl font-semibold text-[#004060]">Your core values are:</p>
            <div className="flex flex-wrap gap-3">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="px-4 py-2 bg-blue-50 text-[#236caf] font-medium rounded-full"
                >
                  {value}
                </div>
              ))}
            </div>
            <p className="text-gray-700 font-medium">Now it is time to put them to work.</p>
          </div>

          {/* The Alignment Engine */}
          <div className="space-y-5">
            <h2 className="text-2xl font-bold text-[#004060]">The Alignment Engine</h2>

            <p className="text-gray-700 leading-relaxed">
              The Alignment Engine is six questions that turn your values from words on a page into daily action. Anytime you feel stuck, overwhelmed, or unsure what to do next, run the six questions and you will come out the other side with more clarity, confidence, and energy than when you started.
            </p>

            <p className="text-gray-700 leading-relaxed">
              Use them anytime you want to plan your day, get out of a funk, boost your energy, find a creative answer, or make a difficult decision easier.
            </p>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-[#236caf] uppercase tracking-wide">
                Here are the six questions. Work through them in order. The order matters.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#004060] to-[#236caf] rounded-xl p-6 space-y-3">
              {QUESTIONS.map((q) => (
                <div key={q.number} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 text-white text-sm font-bold flex items-center justify-center">
                    {q.number}
                  </span>
                  <p className="text-white leading-relaxed pt-0.5">{q.text}</p>
                </div>
              ))}
            </div>

            <p className="text-gray-700 leading-relaxed">
              The Alignment Engine is net energy positive. By the time you finish question six you will have more clarity and confidence than when you started. That is not an accident. That is the system working.
            </p>
          </div>

          {/* CTA */}
          <div className="space-y-6">

            {/* Start the challenge */}
            <div className="relative overflow-hidden rounded-2xl border-2 border-[#236caf] bg-white p-6 flex flex-col gap-4 shadow-sm">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-1.5 bg-blue-50 text-[#236caf] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full">
                  21 Days
                </div>
                <h2 className="text-xl font-bold text-[#004060] leading-snug">Ready to put it to work?</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Start the 21 Day Unstoppable Challenge and put the Alignment Engine to work today.
                </p>
              </div>
              <button
                onClick={() => navigate('/challenge-overview')}
                className="flex items-center justify-center gap-2 bg-[#236caf] hover:bg-[#1a5490] text-white font-semibold py-2.5 px-5 rounded-xl transition-colors duration-200 text-sm"
              >
                Start the Challenge
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 1-on-1 Alignment Session */}
          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              To schedule a 1-on-1 Alignment Session, pick a time below. In the session we will identify solutions for 2–3 pressing problems in your business.
            </p>
            <iframe
              src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ237pZ2EvR6pCZYVJpM2DZtpoOXULbffUVD-RnAqB_6-gUjgg837t86_dpW7FwwdpqTng8WYpms?gv=true"
              style={{ border: 0 }}
              width="100%"
              height="600"
              frameBorder="0"
              title="Schedule a 1-on-1 Alignment Session"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <Button
              variant="outline"
              onClick={() => navigate('/core-values')}
              className="w-1/2"
            >
              Back
            </Button>

            <Button
              onClick={() => navigate('/dashgrowthloop')}
              className="w-1/2"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
